import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Divider, List, ListItemButton, Typography } from '@mui/material';

import { CenterCircularProgress } from '@europrocurement/flexy-components';
import { FaOptionIcon, chevronRightIcon } from '@europrocurement/l2d-icons';

import { catalogFichePath } from '../../Catalog/routes';
import { LinkedCatalogue, LinkedCataloguesStatus } from './useLinkedCatalogues';
import CatalogueSurchargeLine from './CatalogueSurchargeLine';

type LinkedCataloguesTabProps = {
    catalogues: LinkedCatalogue[];
    status: LinkedCataloguesStatus;
    /** Message affiché lorsque aucun catalogue n'est rattaché. */
    emptyLabel?: string;
    /** Affiche l'offre de chaque catalogue (utile côté package, où elles diffèrent). */
    showOffre?: boolean;
};

type CatalogueSurcharge = NonNullable<LinkedCatalogue['surchargeArticles']>[number];

/**
 * Vrai uniquement si la surcharge porte une valeur effective (libellé, tarif ou
 * quantité) ; les entrées sans surcharge réelle sont ignorées.
 */
const hasSurchargeOverride = (surcharge: CatalogueSurcharge): boolean => {
    const hasLabel = surcharge.libelle !== null && surcharge.libelle !== undefined;
    const hasTarif =
        surcharge.tarif !== null && surcharge.tarif !== undefined && surcharge.tarif !== '';
    const hasQuantite = surcharge.quantite !== null && surcharge.quantite !== undefined;

    return hasLabel || hasTarif || hasQuantite;
};

/** Formate une période `début → fin` à partir de dates ISO (la fin est optionnelle). */
const formatPeriod = (start?: string | null, end?: string | null): string | undefined => {
    const toFr = (value?: string | null): string | null => {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
    };

    const startFr = toFr(start);
    const endFr = toFr(end);

    if (!startFr && !endFr) {
        return undefined;
    }

    return `${startFr ?? '…'} → ${endFr ?? '…'}`;
};

/**
 * Onglet « Catalogues » : liste simple et cliquable des catalogues rattachés
 * (à une offre ou à un package), en remontant pour chacun les surcharges
 * d'articles définies au niveau du catalogue.
 */
const LinkedCataloguesTab: React.FunctionComponent<LinkedCataloguesTabProps> = function (props) {
    const {
        catalogues,
        status,
        emptyLabel = "Aucun catalogue n'est rattaché.",
        showOffre = false,
    } = props;
    const navigate = useNavigate();

    if (status === 'idle' || status === 'loading') {
        return <CenterCircularProgress sx={{ height: '40vh' }} />;
    }

    if (status === 'failed') {
        return (
            <Typography
                color="error"
                sx={{ padding: '20px' }}
            >
                Une erreur est survenue lors de la récupération des catalogues rattachés.
            </Typography>
        );
    }

    const sortedCatalogues = [...catalogues].sort((a, b) =>
        String(a.nomCommercial ?? '').localeCompare(String(b.nomCommercial ?? '')),
    );

    return (
        <Box sx={{ padding: 3 }}>
            <Typography
                fontWeight="bold"
                sx={{ mb: 1 }}
            >
                {`Catalogues rattachés (${sortedCatalogues.length})`}
            </Typography>
            {sortedCatalogues.length === 0 ? (
                <Typography color="text.gris70">{emptyLabel}</Typography>
            ) : (
                <List>
                    {sortedCatalogues.map((catalogue) => {
                        const period = formatPeriod(catalogue.dateDebut, catalogue.dateFin);
                        const surcharges = (catalogue.surchargeArticles ?? []).filter(
                            hasSurchargeOverride,
                        );

                        return (
                            <ListItemButton
                                key={catalogue.id}
                                onClick={() => navigate(catalogFichePath(catalogue.id as number))}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    gap: '8px',
                                    borderRadius: '8px',
                                    mb: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                        }}
                                    >
                                        <Typography
                                            color="text.gris70"
                                            fontWeight={600}
                                        >
                                            {catalogue.nomCommercial ?? 'Catalogue'}
                                        </Typography>
                                        {showOffre && catalogue.offre?.libelle ? (
                                            <Chip
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                label={`Offre : ${catalogue.offre.libelle}`}
                                            />
                                        ) : null}
                                        {period ? (
                                            <Typography
                                                variant="l2dbodyxs"
                                                color="text.gris50"
                                            >
                                                {period}
                                            </Typography>
                                        ) : null}
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`${surcharges.length} surcharge${
                                                surcharges.length > 1 ? 's' : ''
                                            }`}
                                        />
                                    </Box>
                                    <FaOptionIcon {...chevronRightIcon.props} />
                                </Box>

                                {surcharges.length > 0 ? (
                                    <Box sx={{ width: '100%', pl: 1 }}>
                                        <Divider sx={{ mb: 0.5 }} />
                                        {surcharges.map((surcharge, index) => (
                                            <CatalogueSurchargeLine
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${catalogue.id}_surcharge_${index}`}
                                                surcharge={surcharge}
                                            />
                                        ))}
                                    </Box>
                                ) : null}
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default LinkedCataloguesTab;
